// polyfills
import 'url-polyfill'
import 'whatwg-fetch'
// should be after the fetch polyfill
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'

// This code helps us to work around the 'illegal invocation' error. Remove once
// the problem has been resolved upstream.
import 'src/utils/ios_workaround'

// libs
import * as Sentry from '@sentry/react'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import {
  Route,
  RouteComponentProps,
  BrowserRouter as Router
} from 'react-router-dom'
import { ThunkDispatch } from 'redux-thunk'

// Sentry
import 'src/utils/reporting/sentry'

// classes
import UserTagmanagerData from 'src/containers/analytics/tag_manager'
import GlobalSnackBar from 'src/containers/global_snack_bar'
import RevisionCheck from 'src/containers/revision_check'
import URLProvider from 'src/containers/url_provider/url_provider'
import UserAuth from 'src/containers/user_auth'
import { Routes } from 'src/routes'

// utils
import { AuthenticationHelper } from 'src//utils/authentication/authentication'
import {
  changeAppLocation,
  setNavSource
} from 'src/actions/app-state/app-state'
import { getStore } from 'src/store/configure-store'
import { getConfigValue } from 'src/utils/config/config'
import initTracker, {
  LOGIN
} from 'src/utils/reporting/events_tracking/events_tracking'
import { loadAndApplyRuntimeColors } from 'src/utils/theme/runtime_colors'
import { parseQueryString } from 'src/utils/url/url'

// interfaces / constants
import { IAction } from 'src/actions/'
import {
  SETTINGS_ACTION_LOGIN,
  SETTINGS_ACTION_REGISTRATION,
  SETTINGS_REFERRAL_APPLE,
  SETTINGS_REFERRAL_APPS,
  SETTINGS_REFERRAL_FACEBOOK,
  SETTINGS_STATUS_SUCCESS,
  SettingsReferral
} from 'src/constants/settings'
import { IOAuth2Credentials } from 'src/interfaces/oauth2'
import { IRootState } from 'src/reducers/interface'

import {
  FeaturesProvider,
  MailboxWebSocketChannel,
  parseFeatureConfig
} from '@lopo-tech/frontends-shared'
import { ApiProvider, TokenResponseType } from '@lopo-tech/lopo-api'
import defaultFeatures from 'features.json'
import TagManager from 'react-gtm-module'
import 'src/fonts/fonts.scss'
import 'src/index.scss'
import Cookiebot from './containers/cookiebot/cookiebot'
import { FeatureConfig } from './containers/featureConfig/featureConfig'
import { WebSocketProviderWrapper } from './containers/smart_components/WebSocketProviderWrapper'
import { UserDataProvider } from './containers/user_data_provider'
import { AuthProvider, useAuthContext } from './context/authContext'
import { ILocationShape } from './interfaces/location'
import { StoredLocationHelper } from './utils/location_storage/location_storage'

const gtmId = getConfigValue('gtm.id')
gtmId && TagManager.initialize({ gtmId })

const loginTracker = initTracker(LOGIN)
const store = getStore()

const checkCredentials = ({
  accessToken,
  createdAt,
  expiresIn,
  refreshToken
}: IOAuth2Credentials) => {
  if (
    !accessToken ||
    !createdAt ||
    isNaN(createdAt) ||
    !expiresIn ||
    isNaN(expiresIn) ||
    !refreshToken
  ) {
    return undefined
  }

  if (isNaN(createdAt) || isNaN(expiresIn)) {
    return undefined
  }

  return { accessToken, createdAt, expiresIn, refreshToken }
}

const initializeLocation = () => {
  const defaultAddress = getConfigValue<{
    locationShape: ILocationShape;
    latitude: number;
    longitude: number;
  }>('featureFlags.location.defaultAddress')

  const storedLocation = StoredLocationHelper.getLastLocation()

  if (storedLocation) {
    (store.dispatch as ThunkDispatch<IRootState, unknown, IAction>)(
      changeAppLocation(
        storedLocation
      ))
    return
  }

  if (defaultAddress) {
    (store.dispatch as ThunkDispatch<IRootState, unknown, IAction>)(
      changeAppLocation(
        defaultAddress.locationShape,
        defaultAddress.locationShape.boundingBox
      )
    )
  }
}

const params = parseQueryString<
  string,
  {
    as: string;
    action: string;
    ref: SettingsReferral;
    access_token: string;
    created_at: string;
    expires_in: string;
    'nav-source': string;
    refresh_token: string;
    status: string;
  }
>(window.location.search, [
  'as',
  'action',
  'ref',
  'access_token',
  'created_at',
  'expires_in',
  'nav-source',
  'refresh_token',
  'status'
])

const navSource = params['nav-source']

const isAppleAuth = params.ref === SETTINGS_REFERRAL_APPLE
const isAppsAuth = params.ref === SETTINGS_REFERRAL_APPS
const isFacebookAuth = params.ref === SETTINGS_REFERRAL_FACEBOOK

const credentials =
  (params.action === SETTINGS_ACTION_LOGIN ||
    params.action === SETTINGS_ACTION_REGISTRATION) &&
  (isAppleAuth || isAppsAuth || isFacebookAuth) &&
  params.status === SETTINGS_STATUS_SUCCESS
    ? {
      accessToken: params.access_token,
      createdAt: parseInt(params.created_at, 10),
      expiresIn: parseInt(params.expires_in, 10),
      refreshToken: params.refresh_token
    }
    : undefined

if (credentials && checkCredentials(credentials)) {
  AuthenticationHelper.storeOAuth2Credentials(credentials)
}
if (AuthenticationHelper.checkTokenValidity()) {
  const trackingAction = isAppleAuth
    ? LOGIN.ACTIONS.APPLE
    : isAppsAuth
      ? LOGIN.ACTIONS.APPS
      : isFacebookAuth && LOGIN.ACTIONS.FACEBOOK
  if (trackingAction) {
    loginTracker(trackingAction, LOGIN.LABELS.FINISH)
  }
}

initializeLocation()

navSource &&
  (store.dispatch as ThunkDispatch<IRootState, {}, IAction>)(
    setNavSource(navSource)
  )

const renderUserAuth = (route: RouteComponentProps) => {
  return <UserAuth history={route.history} search={route.location.search} />
}

const onTokenChange = (token: TokenResponseType) => {
  if (
    token.access_token &&
    token.refresh_token &&
    token.created_at &&
    token.expires_in
  ) {
    const credentials: IOAuth2Credentials = {
      accessToken: token.access_token,
      refreshToken: token.refresh_token,
      createdAt: token.created_at,
      expiresIn: token.expires_in
    }
    AuthenticationHelper.storeOAuth2Credentials(credentials)
    loginTracker(LOGIN.ACTIONS.EMAIL, LOGIN.LABELS.FINISH)
  } else {
    AuthenticationHelper.logout()
  }
}

const AppContent = () => {
  const { selectedProfileId } = useAuthContext()

  return (
    <ApiProvider
      baseUrl={process.env.API_HOST!}
      clientId={window.js_env.oauth_client_id}
      onTokenChange={onTokenChange}
      selectedProfileId={selectedProfileId}
    >
      <UserDataProvider>
        <WebSocketProviderWrapper>
          <Router>
            <URLProvider>
              <>
                <Cookiebot />
                <FeatureConfig />
                <UserTagmanagerData />
                <Route render={renderUserAuth} />
                {/* !isNavSourceFromApp(navSource) && <CookieConsent /> */}
                <Route component={Routes} />
                <RevisionCheck />
                <MailboxWebSocketChannel />
                <GlobalSnackBar />
              </>
            </URLProvider>
          </Router>
        </WebSocketProviderWrapper>
      </UserDataProvider>
    </ApiProvider>
  )
}

const App = () => (
  <Sentry.ErrorBoundary fallback={<p>An error occurred</p>}>
    <FeaturesProvider
      baseUrl={process.env.API_HOST!}
      defaultFeatures={parseFeatureConfig(defaultFeatures)}
    >
      <Provider store={store}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Provider>
    </FeaturesProvider>
  </Sentry.ErrorBoundary>
)

const bootstrap = async () => {
  await loadAndApplyRuntimeColors()
  ReactDOM.render(<App />, document.getElementById('lopo-react-root'))
}

bootstrap()
